import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircle, XCircle, Loader2, BookOpen, Award } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, getAuthHeader } = useAuth();
  const [status, setStatus] = useState('checking');
  const [message, setMessage] = useState('Verifying your payment...');
  const [courseId, setCourseId] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId && user) {
      checkPaymentStatus();
    } else if (!sessionId) {
      setStatus('error');
      setMessage('Invalid payment session');
    }
  }, [sessionId, user]);

  const checkPaymentStatus = async () => {
    if (pollCount >= 10) {
      setStatus('timeout');
      setMessage('Payment verification timed out. Please check your email for confirmation.');
      return;
    }

    try {
      const response = await axios.get(
        `${API}/payments/status/${sessionId}`,
        getAuthHeader()
      );

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setMessage('Payment successful! You are now enrolled.');
        setCourseId(response.data.course_id);
        toast.success('🎉 Payment successful! Welcome to the course!');
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        setMessage('Payment session expired. Please try again.');
      } else {
        // Continue polling
        setPollCount(prev => prev + 1);
        setTimeout(checkPaymentStatus, 2000);
      }
    } catch (error) {
      console.error('Error checking payment:', error);
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Error verifying payment');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
        {status === 'checking' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 bg-indigo-100 rounded-full flex items-center justify-center">
              <Loader2 className="w-10 h-10 text-indigo-600 animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Processing Payment</h1>
            <p className="text-slate-600 mb-6">{message}</p>
            <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-indigo-600 rounded-full transition-all duration-500"
                style={{ width: `${Math.min(pollCount * 10, 90)}%` }}
              ></div>
            </div>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Payment Successful!</h1>
            <p className="text-slate-600 mb-6">{message}</p>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-center space-x-2 text-green-700">
                <Award className="w-5 h-5" />
                <span className="font-semibold">Points Earned!</span>
              </div>
              <p className="text-sm text-green-600 mt-1">
                You've earned bonus points for enrolling in this course.
              </p>
            </div>
            
            <div className="space-y-3">
              {courseId && (
                <Button
                  data-testid="view-course-btn"
                  onClick={() => navigate(`/course/${courseId}`)}
                  className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700"
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  Start Learning
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => navigate('/student/dashboard')}
                className="w-full rounded-full"
              >
                Go to Dashboard
              </Button>
            </div>
          </>
        )}

        {status === 'expired' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 bg-amber-100 rounded-full flex items-center justify-center">
              <XCircle className="w-10 h-10 text-amber-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Session Expired</h1>
            <p className="text-slate-600 mb-6">{message}</p>
            <Button
              onClick={() => navigate('/courses')}
              className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700"
            >
              Browse Courses
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Payment Error</h1>
            <p className="text-slate-600 mb-6">{message}</p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/courses')}
                className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700"
              >
                Try Again
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/student/dashboard')}
                className="w-full rounded-full"
              >
                Go to Dashboard
              </Button>
            </div>
          </>
        )}

        {status === 'timeout' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 bg-amber-100 rounded-full flex items-center justify-center">
              <Loader2 className="w-10 h-10 text-amber-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Verification Timeout</h1>
            <p className="text-slate-600 mb-6">{message}</p>
            <div className="space-y-3">
              <Button
                onClick={() => {
                  setPollCount(0);
                  setStatus('checking');
                  checkPaymentStatus();
                }}
                className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700"
              >
                Retry Verification
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/student/dashboard')}
                className="w-full rounded-full"
              >
                Go to Dashboard
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
